import { useEffect, useState } from "react";
import { useForm } from "../../useForm";

import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import Input from "../../../controls/Input";
import SelectMenu from "../../../controls/SelectMenu";
import { serviceTypes } from "../../../../shared/utility";

const initialFValues = {
  name: "",
  type: -1,
  show_options: 0,
  multi_options: [],
};

export default function ServiceDetailsForm(props) {
  const {
    actionCall,
    serviceId,
    isEdit,
    setOpen,
    title,
    getServiceApiCall,
    addNewServiceOptionApiCall,
    updateServiceOptionApiCall,
    removeServiceOptionApiCall,
  } = props;

  const [newOption, setNewOption] = useState("");

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false
  );

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    if ("name" in fieldValues) {
      temp.name = null;
      if (fieldValues.name.value === -1) {
        temp.name = "This field is required.";
      }
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      actionCall({
        ...values,
      });
    }
  };

  const getServiceDetails = async function () {
    const callResult = await getServiceApiCall(serviceId);
    if (callResult.success) {
      const service = callResult.data.service;
      var typeSelected = [];
      typeSelected = serviceTypes.find((el) => el.value === service.type);

      setValues({
        ...service,
        type: typeSelected,
      });
    }
  };

  useEffect(() => {
    if (isEdit) {
      getServiceDetails();
    }
  }, []);

  const addNewServiceOption = async function () {
    if (newOption.length > 0 && serviceId > 0) {
      let callResult = await addNewServiceOptionApiCall(serviceId, newOption);
      if (callResult.success) {
        const new_option = callResult.data.option;
        setValues({
          ...values,
          multi_options: [...values.multi_options, new_option],
        });
        setNewOption("");
      }
    }
  };

  return (
    <form className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
        <div className="px-4 sm:px-6">
          <div className="flex items-start justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              {title}
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={() => setOpen({ childComponent: null, open: false })}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        <div className="relative mt-6 flex-1 px-4 sm:px-6 space-y-5">
          <Input
            label="Name"
            name="name"
            onChange={handleInputChange}
            value={values.name}
            labelOn={true}
            type="text"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
          />

          <SelectMenu
            parentClass="w-48"
            handleInputChange={handleInputChange}
            name="type"
            options={serviceTypes}
            value={values.type}
            title={"Type"}
            error={errors.type}
            showLabel={true}
          />

          <div>
            <fieldset>
              <legend className="text-sm/6 font-semibold text-gray-900">
                Show options
              </legend>
              <div className="mt-2 space-y-6 sm:flex sm:items-center sm:space-y-0 sm:space-x-10">
                <div className="flex items-center">
                  <input
                    checked={values.show_options === 0}
                    name="option-input"
                    type="radio"
                    className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
                    onClick={(e) => setValues({ ...values, show_options: 0 })}
                  />
                  <label
                    htmlFor="option-input"
                    className="ml-3 block text-sm/6 font-medium text-gray-900"
                  >
                    No
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    checked={values.show_options === 1}
                    name="option-multiple"
                    type="radio"
                    className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
                    onClick={(e) => setValues({ ...values, show_options: 1 })}
                  />
                  <label
                    htmlFor="option-multiple"
                    className="ml-3 block text-sm/6 font-medium text-gray-900"
                  >
                    Yes
                  </label>
                </div>
              </div>
            </fieldset>
          </div>

          <div>
            {values.show_options === 1 ? (
              <div className="space-y-4">
                <div className="space-y-4">
                  <legend className="text-sm/6 font-semibold text-gray-900">
                    Options
                  </legend>
                  {values.multi_options.map((option, index) => (
                    <div key={index} className="flex">
                      <Input
                        onChange={(e) => {
                          const updatedOptions = [...values.multi_options];
                          updatedOptions[index] = {
                            ...option,
                            name: e.target.value,
                          };
                          setValues({
                            ...values,
                            multi_options: updatedOptions,
                          });
                        }}
                        value={option.name}
                        labelOn={true}
                        type="text"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      />
                      <button
                        type="button"
                        className="ml-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                        onClick={() => updateServiceOptionApiCall(option)}
                      >
                        Update
                      </button>
                      <button
                        type="button"
                        className="ml-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                        onClick={() => {
                          const updatedOptions = values.multi_options.filter(
                            (_, i) => i !== index
                          );
                          setValues({
                            ...values,
                            multi_options: updatedOptions,
                          });
                          removeServiceOptionApiCall(option);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-sm/6 font-semibold text-gray-900">
                    Add new option
                  </p>
                  <div className="flex">
                    <Input
                      onChange={(e) => setNewOption(e.target.value)}
                      value={newOption}
                      labelOn={true}
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    />
                    <button
                      type="button"
                      className="ml-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                      onClick={addNewServiceOption}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
          onClick={() => setOpen({ childComponent: null, open: false })}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          onClick={handleSubmit}
        >
          {isEdit ? "Save" : "Create"}
        </button>
      </div>
    </form>
  );
}
